import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  ShieldCheck, FileText, Bell, Stethoscope, ClipboardList, Send,
  AlertTriangle, CheckCircle2, XCircle, ArrowRight, Lock, Languages, Cpu,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Small presentational helpers (kept local & tiny)
const Section = ({ id, eyebrow, title, children }) => (
  <section id={id} className="max-w-5xl mx-auto px-5 py-10 border-t border-violet-100" data-testid={`section-${id}`}>
    {eyebrow && <p className="text-xs font-bold uppercase tracking-[0.16em] text-violet-700 mb-2">{eyebrow}</p>}
    <h2 className="text-2xl sm:text-3xl font-bold text-violet-950 leading-tight mb-5">{title}</h2>
    {children}
  </section>
);

const Card = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-2xl border border-violet-100 bg-violet-50/40 p-5 transition-colors hover:border-violet-300" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-5 h-5 text-violet-700" />
      <h3 className="font-bold text-violet-950">{title}</h3>
    </div>
    <div className="text-sm text-slate-700 leading-relaxed">{children}</div>
  </div>
);

export default function HowItWorksPage() {
  const [engineCount, setEngineCount] = useState(307);
  useEffect(() => {
    axios.get(`${BACKEND}/api/system/build-status`)
      .then(r => { const n = r?.data?.catalog_engines; if (typeof n === 'number') setEngineCount(n); })
      .catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-white text-slate-900 font-serif" data-testid="how-it-works-page" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-violet-100">
        <div className="max-w-5xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="font-bold text-violet-950 text-sm sm:text-base" data-testid="brand-home-link">
            P.Ai Global Health <span className="text-violet-500 font-normal">(Patient AI)</span>
          </Link>
          <Link
            to="/"
            className="text-xs sm:text-sm font-bold rounded-full bg-violet-700 text-white px-4 py-2 hover:bg-violet-800 transition-colors"
            data-testid="start-intake-cta"
          >
            Start an intake <ArrowRight className="inline w-4 h-4 ml-1" />
          </Link>
        </div>
      </header>

      {/* Demo banner */}
      <div className="max-w-5xl mx-auto px-5 pt-6">
        <div className="rounded-lg border border-red-200 bg-red-50 border-l-4 border-l-red-600 px-4 py-2.5 text-[12.5px] text-red-900" data-testid="demo-banner">
          <span className="font-bold uppercase tracking-wide text-red-600 text-[11px]">Demo prototype · No PHI</span> — this is a synthetic-data sandbox, not HIPAA-compliant. The production architecture is designed to be HIPAA-Eligible.
        </div>
      </div>

      {/* Hero / one-liner */}
      <div className="max-w-5xl mx-auto px-5 pt-6 pb-2">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-violet-700 mb-3">What it is — and isn't</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-violet-950 leading-[1.1] mb-4">
          Patient-centric clinical intelligence.<br />
          <span className="text-violet-600" data-testid="hiw-engine-count-headline">{engineCount} deterministic engines. Zero LLMs in the diagnostic loop.</span>
        </h1>
        <p className="text-base text-slate-700 leading-relaxed max-w-3xl">
          P.Ai Global Health (Patient AI) turns what a patient says — by voice or text, in 25 languages,
          structured with <strong>OLDCARTS / OPQRST</strong> — into structured clinical evidence using {engineCount}{" "}
          hand-coded deterministic engines, each citing a named guideline. It is <strong>decision-support,
          not a diagnosis.</strong> The physician always signs.
        </p>
        <div className="flex flex-wrap gap-2 mt-5">
          {[
            { icon: Cpu, t: "Deterministic — byte-identical re-runs" },
            { icon: ShieldCheck, t: "Zero LLMs in diagnosis" },
            { icon: Languages, t: "25 languages, multi-modal" },
          ].map((b, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 text-xs font-bold text-violet-800 bg-violet-100 rounded-full px-3 py-1.5">
              <b.icon className="w-3.5 h-3.5" /> {b.t}
            </span>
          ))}
        </div>
      </div>

      {/* Patient side */}
      <Section id="patient" eyebrow="For the patient — free, forever" title="The patient side">
        <div className="grid md:grid-cols-3 gap-4">
          <Card icon={Lock} title="Vault intake (OLDCARTS / OPQRST)" testid="card-vault-intake">
            A patient-owned health record. Multi-modal, language-agnostic structured intake.
            <span className="text-violet-800 font-semibold"> Designed to be HIPAA-Eligible in production.</span>
          </Card>
          <Card icon={FileText} title="History summary (no SOAP)" testid="card-history-summary">
            Organizes the patient's <em>own</em> history — it does <strong>not</strong> generate a clinical SOAP note for the patient. That's the physician's tool, not the patient's.
          </Card>
          <Card icon={Bell} title="24/7 evidence-based alerts" testid="card-247-alerts">
            <strong>Not an AI doctor.</strong> Asks a few questions, then tells the patient <em>when &amp; where</em> to seek care — ER, urgent care, or routine. Escalation-biased; never discourages emergency care.
          </Card>
        </div>

        {/* Worked example */}
        <div className="mt-6 rounded-2xl border-2 border-violet-200 bg-violet-50/60 p-5" data-testid="alert-example">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-violet-700 mb-2">Worked example — the 24/7 alert in action</p>
          <p className="text-sm text-slate-800 leading-relaxed">
            <strong>Maria, 58, diabetic</strong>, opens P.Ai Global Health (Patient AI):
            <em> "Burning ache in my upper stomach for an hour — I thought it was indigestion."</em>
          </p>
          <p className="text-sm text-slate-800 leading-relaxed mt-3">It asks 4 quick questions:</p>
          <ul className="text-sm text-slate-700 leading-relaxed mt-1 list-disc pl-5 space-y-0.5">
            <li>Does it spread to your chest, arm, jaw, neck or back?</li>
            <li>Any sweating, breathlessness or nausea?</li>
            <li>Did it start or worsen with exertion?</li>
            <li>Any diabetes, high blood pressure, high cholesterol or heart history?</li>
          </ul>
          <p className="text-sm text-slate-800 leading-relaxed mt-3">
            She answers: <em>spreads to the jaw, a little sweaty, started on the stairs, diabetic.</em>
          </p>
          <div className="mt-3 rounded-lg bg-red-50 border-l-4 border-red-500 px-4 py-3 text-sm text-red-900">
            <span className="font-bold">Evidence-based alert (not a diagnosis):</span> "These features — jaw radiation,
            sweating, exertional onset, with diabetes — can signal a heart problem <em>even when it feels like
            indigestion.</em> Call emergency services or go to the ER now."
          </div>
          <p className="text-[13px] text-slate-600 leading-relaxed mt-3">
            <strong>The teaching point:</strong> in people with diabetes, women, and older adults, a heart attack often
            masquerades as epigastric "indigestion." P.Ai Global Health (Patient AI) is escalation-biased — it never says
            "you're having a heart attack" (the physician's call); it says "these features warrant emergency evaluation now."
            With <strong>no</strong> red flags it de-escalates (self-care + see your doctor) — so it's calibrated, not "everyone to the ER."
          </p>
        </div>
      </Section>

      {/* Physician side */}
      <Section id="physician" eyebrow="For the physician — pre-visit triage" title="The physician side">
        <div className="grid md:grid-cols-3 gap-4">
          <Card icon={ClipboardList} title="1–2 page pre-visit summary" testid="card-previsit-summary">
            Chief complaint + a summary of past medical history and surgical history — organized before the visit so the clinician walks in oriented.
          </Card>
          <Card icon={Stethoscope} title="Two SOAP formats" testid="card-two-soap">
            A <strong>Dr. V. N. Devarajan</strong> history-forward note, and a <strong>full SOAP</strong> for those who want it — with a worst-first differential.
          </Card>
          <Card icon={Send} title="SBAR hand-off" testid="card-sbar">
            A deterministic <strong>SBAR</strong> for EHR import or nurse hand-off — hash-chained for audit replay.
          </Card>
        </div>
      </Section>

      {/* What it is NOT */}
      <Section id="not" eyebrow="Boundaries" title="What it is NOT">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            "Not a chatbot.",
            "Not an LLM making the diagnosis.",
            "Not a replacement for the physician.",
          ].map((t, i) => (
            <div key={i} className="flex items-start gap-2 rounded-xl border border-slate-200 p-4" data-testid={`not-item-${i}`}>
              <XCircle className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
              <span className="text-sm font-semibold text-slate-700">{t}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Why validate */}
      <Section id="validate" eyebrow="For clinical validators" title="Why we ask for your feedback">
        <div className="rounded-2xl border border-green-200 bg-green-50/60 p-5" data-testid="validate-callout">
          <p className="text-sm text-slate-800 leading-relaxed">
            The purpose of clinical validation is to assess the <strong>deterministic clinical engines</strong> — are the
            flags right, is the severity right, is anything missed or over-fired? Bring your <em>own</em> cases and be blunt;
            we don't coach the test. Your feedback hardens the engines for production — including the deterministic,
            evidence-based <strong>ambient scribing</strong>.
          </p>
          <div className="flex flex-wrap gap-3 mt-4">
            <a
              href={`${BACKEND ? "" : ""}/register/validator`}
              className="inline-flex items-center gap-1.5 text-sm font-bold rounded-full bg-violet-700 text-white px-5 py-2.5 hover:bg-violet-800 transition-colors"
              data-testid="register-validator-cta"
            >
              <CheckCircle2 className="w-4 h-4" /> Register as a clinical validator
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-bold rounded-full border border-violet-300 text-violet-800 px-5 py-2.5 hover:bg-violet-50 transition-colors"
              data-testid="run-a-case-cta"
            >
              Run a case <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="max-w-5xl mx-auto px-5 py-10 border-t border-violet-100">
        <p className="text-[13px] text-slate-500 leading-relaxed flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          P.Ai Global Health (Patient AI) · pre-pilot prototype · decision-support, not a diagnosis · physician sign-off
          required · no PHI in this demo environment. Live system metrics:{" "}
          <a href={`${BACKEND}/api/system/build-status`} className="text-violet-700 underline" data-testid="build-status-link" target="_blank" rel="noreferrer">
            /api/system/build-status
          </a>
        </p>
      </footer>
    </div>
  );
}
